import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Server, Database, Shield, Globe, Cpu, HardDrive, Radio, Activity, 
  Layers, MonitorCheck, ZoomIn, ZoomOut, Maximize, Search, Filter,
  Eye, EyeOff, Settings, BarChart3, AlertTriangle, CheckCircle,
  WifiOff, Wifi, Lock, Unlock, Cloud, Router, Smartphone, Laptop,
  ChevronLeft, Play, Pause, RotateCcw, Download, Upload, Zap
} from 'lucide-react';

const InfrastructureDocumentation = () => {
  // États principaux
  const [zoom, setZoom] = useState(1);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [viewMode, setViewMode] = useState('topology'); // topology, logical, physical
  const [showConnections, setShowConnections] = useState(true);
  const [showMetrics, setShowMetrics] = useState(true);
  const [animationSpeed, setAnimationSpeed] = useState(1);
  const [filterZone, setFilterZone] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isPaused, setIsPaused] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  
  // États de navigation
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastPanOffset, setLastPanOffset] = useState({ x: 0, y: 0 });
  
  // États temps réel
  const [liveMetrics, setLiveMetrics] = useState({});
  const [alerts, setAlerts] = useState([]);
  
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  // Navigation handler
  const navigateToPage = (e, pageName) => {
    e.preventDefault();
    if (window.onNavigateToPage) {
      window.onNavigateToPage(pageName);
    }
  };

  // Gestionnaires de navigation/panning
  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('.infrastructure-component') || e.target.closest('.toolbar') || e.target.closest('.zoom-controls')) {
      return; // Ne pas commencer le drag sur les composants ou contrôles
    }
    
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setLastPanOffset(panOffset);
    document.body.style.cursor = 'grabbing';
  }, [panOffset]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    
    setPanOffset({
      x: lastPanOffset.x + deltaX,
      y: lastPanOffset.y + deltaY
    });
  }, [isDragging, dragStart, lastPanOffset]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    document.body.style.cursor = 'default';
  }, []);

  const centerView = useCallback(() => {
    // Calculer le centre de l'infrastructure
    const containerWidth = window.innerWidth;
    const containerHeight = window.innerHeight - 140; // Moins la hauteur de la toolbar
    
    // Taille approximative de l'infrastructure
    const infraWidth = 900;
    const infraHeight = 900;
    
    setPanOffset({
      x: (containerWidth - infraWidth * zoom) / 2,
      y: (containerHeight - infraHeight * zoom) / 2 + 50 // Petit offset vers le haut
    });
  }, [zoom]);

  const resetView = useCallback(() => {
    setZoom(1);
    centerView();
  }, [centerView]);

  // Centrer la vue au démarrage
  useEffect(() => {
    const timer = setTimeout(() => {
      centerView();
    }, 100);
    return () => clearTimeout(timer);
  }, [centerView]);

  // Gestionnaires d'événements globaux
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Configuration avancée des composants
  const COMPONENT_TYPES = {
    LOAD_BALANCER: {
      icon: '⚖️',
      color: '#06b6d4',
      bgGradient: 'from-cyan-500 to-blue-500',
      category: 'network',
      description: 'Répartit la charge entre serveurs'
    },
    FIREWALL: {
      icon: '🔥',
      color: '#ef4444',
      bgGradient: 'from-red-500 to-orange-500',
      category: 'security',
      description: 'Protection périmétrique'
    },
    API_GATEWAY: {
      icon: '🚪',
      color: '#8b5cf6',
      bgGradient: 'from-purple-500 to-indigo-500',
      category: 'network',
      description: 'Point d\'entrée API unifié'
    },
    APP_SERVER: {
      icon: '⚙️',
      color: '#10b981',
      bgGradient: 'from-emerald-500 to-green-500',
      category: 'compute',
      description: 'Serveur d\'application'
    },
    DATABASE: {
      icon: '🗄️',
      color: '#3b82f6',
      bgGradient: 'from-blue-500 to-indigo-500',
      category: 'storage',
      description: 'Base de données'
    },
    CACHE: {
      icon: '⚡',
      color: '#f59e0b',
      bgGradient: 'from-amber-500 to-yellow-500',
      category: 'storage',
      description: 'Cache haute performance'
    },
    QUEUE: {
      icon: '📬',
      color: '#ec4899',
      bgGradient: 'from-pink-500 to-rose-500',
      category: 'messaging',
      description: 'File de messages'
    },
    CDN: {
      icon: '🌐',
      color: '#06b6d4',
      bgGradient: 'from-cyan-500 to-teal-500',
      category: 'network',
      description: 'Content Delivery Network'
    },
    MONITORING: {
      icon: '📊',
      color: '#8b5cf6',
      bgGradient: 'from-violet-500 to-purple-500',
      category: 'observability',
      description: 'Monitoring et métriques'
    }
  };

  // Protocoles de communication avancés
  const PROTOCOLS = {
    HTTPS: {
      name: 'HTTPS',
      port: 443,
      color: '#10b981',
      pattern: 'solid',
      encrypted: true,
      description: 'HTTP sécurisé avec TLS',
      icon: '🔒'
    },
    HTTP: {
      name: 'HTTP',
      port: 80,
      color: '#6b7280',
      pattern: 'dashed',
      encrypted: false,
      description: 'HTTP standard',
      icon: '🌐'
    },
    TCP: {
      name: 'TCP',
      port: 'variable',
      color: '#3b82f6',
      pattern: 'solid',
      encrypted: false,
      description: 'Transmission Control Protocol',
      icon: '🔗'
    },
    WEBSOCKET: {
      name: 'WebSocket',
      port: 'variable',
      color: '#8b5cf6',
      pattern: 'dotted',
      encrypted: false,
      description: 'Communication bidirectionnelle',
      icon: '🔄'
    },
    GRPC: {
      name: 'gRPC',
      port: 'variable',
      color: '#f59e0b',
      pattern: 'solid',
      encrypted: true,
      description: 'RPC haute performance',
      icon: '⚡'
    }
  };

  // Infrastructure complète
  const INFRASTRUCTURE = {
    zones: {
      internet: {
        name: 'Internet',
        color: '#ef4444',
        x: 50, y: 50, width: 300, height: 120
      },
      dmz: {
        name: 'DMZ',
        color: '#f59e0b',
        x: 50, y: 220, width: 800, height: 180
      },
      private: {
        name: 'Zone Privée',
        color: '#10b981',
        x: 50, y: 450, width: 1000, height: 350
      },
      data: {
        name: 'Zone Data',
        color: '#3b82f6',
        x: 50, y: 850, width: 800, height: 200
      }
    },
    components: {
      cdn: {
        id: 'cdn',
        name: 'CloudFlare CDN',
        type: 'CDN',
        zone: 'internet',
        x: 150, y: 80,
        ip: '1.1.1.1',
        status: 'active',
        software: [
          { name: 'CloudFlare Edge', version: '2024.1', status: 'running', cpu: 2, memory: 15 }
        ],
        specs: { cpu: '∞', memory: '∞', network: '100Gbps' },
        connections: ['firewall']
      },
      firewall: {
        id: 'firewall',
        name: 'Palo Alto FW',
        type: 'FIREWALL',
        zone: 'dmz',
        x: 100, y: 260,
        ip: '10.0.1.1',
        status: 'active',
        software: [
          { name: 'PAN-OS', version: '11.0', status: 'running', cpu: 45, memory: 60 },
          { name: 'Threat Prevention', version: '8234-7654', status: 'running', cpu: 30, memory: 40 }
        ],
        specs: { cpu: '8 vCPU', memory: '32GB', network: '10Gbps' },
        connections: ['loadbalancer']
      },
      loadbalancer: {
        id: 'loadbalancer',
        name: 'HAProxy LB',
        type: 'LOAD_BALANCER',
        zone: 'dmz',
        x: 450, y: 260,
        ip: '10.0.1.10',
        status: 'active',
        software: [
          { name: 'HAProxy', version: '2.8.3', status: 'running', cpu: 25, memory: 30 },
          { name: 'Keepalived', version: '2.2.8', status: 'running', cpu: 5, memory: 10 }
        ],
        specs: { cpu: '4 vCPU', memory: '16GB', network: '10Gbps' },
        connections: ['gateway', 'app1', 'app2']
      },
      gateway: {
        id: 'gateway',
        name: 'Kong Gateway',
        type: 'API_GATEWAY',
        zone: 'private',
        x: 100, y: 490,
        ip: '10.0.2.20',
        status: 'active',
        software: [
          { name: 'Kong Gateway', version: '3.4.2', status: 'running', cpu: 35, memory: 45 },
          { name: 'Kong Manager', version: '3.4.2', status: 'running', cpu: 15, memory: 25 },
          { name: 'Rate Limiting Plugin', version: '3.4.2', status: 'running', cpu: 10, memory: 15 }
        ],
        specs: { cpu: '6 vCPU', memory: '24GB', network: '1Gbps' },
        connections: ['app1', 'app2', 'cache']
      },
      app1: {
        id: 'app1',
        name: 'KG-Banking API',
        type: 'APP_SERVER',
        zone: 'private',
        x: 350, y: 490,
        ip: '10.0.2.100',
        status: 'active',
        software: [
          { name: 'Node.js', version: '20.10.0', status: 'running', cpu: 40, memory: 55 },
          { name: 'Express.js', version: '4.18.2', status: 'running', cpu: 20, memory: 30 },
          { name: 'PM2', version: '5.3.0', status: 'running', cpu: 5, memory: 10 },
          { name: 'New Relic Agent', version: '11.7.0', status: 'running', cpu: 5, memory: 15 }
        ],
        specs: { cpu: '8 vCPU', memory: '32GB', storage: '500GB SSD' },
        connections: ['database', 'cache', 'queue']
      },
      app2: {
        id: 'app2',
        name: 'KG-Banking API-2',
        type: 'APP_SERVER',
        zone: 'private',
        x: 600, y: 490,
        ip: '10.0.2.101',
        status: 'active',
        software: [
          { name: 'Node.js', version: '20.10.0', status: 'running', cpu: 38, memory: 52 },
          { name: 'Express.js', version: '4.18.2', status: 'running', cpu: 22, memory: 28 },
          { name: 'PM2', version: '5.3.0', status: 'running', cpu: 3, memory: 8 },
          { name: 'New Relic Agent', version: '11.7.0', status: 'running', cpu: 7, memory: 12 }
        ],
        specs: { cpu: '8 vCPU', memory: '32GB', storage: '500GB SSD' },
        connections: ['database', 'cache', 'queue']
      },
      cache: {
        id: 'cache',
        name: 'Redis Cluster',
        type: 'CACHE',
        zone: 'private',
        x: 100, y: 680,
        ip: '10.0.2.200',
        status: 'active',
        software: [
          { name: 'Redis', version: '7.2.3', status: 'running', cpu: 30, memory: 75 },
          { name: 'Redis Sentinel', version: '7.2.3', status: 'running', cpu: 5, memory: 10 },
          { name: 'Redis Exporter', version: '1.55.0', status: 'running', cpu: 2, memory: 5 }
        ],
        specs: { cpu: '4 vCPU', memory: '64GB', storage: '1TB NVMe' },
        connections: []
      },
      queue: {
        id: 'queue',
        name: 'RabbitMQ Cluster',
        type: 'QUEUE',
        zone: 'private',
        x: 600, y: 680,
        ip: '10.0.2.210',
        status: 'active',
        software: [
          { name: 'RabbitMQ', version: '3.12.8', status: 'running', cpu: 25, memory: 40 },
          { name: 'RabbitMQ Management', version: '3.12.8', status: 'running', cpu: 10, memory: 15 },
          { name: 'Prometheus Plugin', version: '3.12.8', status: 'running', cpu: 5, memory: 8 }
        ],
        specs: { cpu: '4 vCPU', memory: '16GB', storage: '200GB SSD' },
        connections: ['monitoring']
      },
      database: {
        id: 'database',
        name: 'PostgreSQL Primary',
        type: 'DATABASE',
        zone: 'data',
        x: 150, y: 880,
        ip: '10.0.3.100',
        status: 'active',
        software: [
          { name: 'PostgreSQL', version: '16.1', status: 'running', cpu: 45, memory: 70 },
          { name: 'pgBouncer', version: '1.21.0', status: 'running', cpu: 5, memory: 5 },
          { name: 'WAL-G', version: '2.0.1', status: 'running', cpu: 10, memory: 8 },
          { name: 'pg_stat_statements', version: '1.10', status: 'running', cpu: 2, memory: 3 }
        ],
        specs: { cpu: '16 vCPU', memory: '128GB', storage: '4TB NVMe RAID10' },
        connections: ['database_replica']
      },
      database_replica: {
        id: 'database_replica',
        name: 'PostgreSQL Replica',
        type: 'DATABASE',
        zone: 'data',
        x: 450, y: 880,
        ip: '10.0.3.101',
        status: 'active',
        software: [
          { name: 'PostgreSQL', version: '16.1', status: 'running', cpu: 20, memory: 45 },
          { name: 'pgBouncer', version: '1.21.0', status: 'running', cpu: 3, memory: 4 },
          { name: 'WAL-G', version: '2.0.1', status: 'running', cpu: 5, memory: 6 }
        ],
        specs: { cpu: '12 vCPU', memory: '96GB', storage: '4TB NVMe RAID10' },
        connections: []
      },
      monitoring: {
        id: 'monitoring',
        name: 'Prometheus Stack',
        type: 'MONITORING',
        zone: 'private',
        x: 850, y: 490,
        ip: '10.0.2.300',
        status: 'active',
        software: [
          { name: 'Prometheus', version: '2.47.2', status: 'running', cpu: 20, memory: 35 },
          { name: 'Grafana', version: '10.2.2', status: 'running', cpu: 15, memory: 25 },
          { name: 'AlertManager', version: '0.26.0', status: 'running', cpu: 5, memory: 10 },
          { name: 'Node Exporter', version: '1.7.0', status: 'running', cpu: 3, memory: 5 }
        ],
        specs: { cpu: '6 vCPU', memory: '48GB', storage: '2TB SSD' },
        connections: []
      }
    }
  };

  // Simulation des métriques temps réel
  useEffect(() => {
    const updateMetrics = () => {
      const newMetrics = {};
      Object.keys(INFRASTRUCTURE.components).forEach(id => {
        newMetrics[id] = {
          cpu: Math.random() * 100,
          memory: Math.random() * 100,
          network: Math.random() * 1000,
          latency: Math.random() * 50 + 10,
          requests: Math.floor(Math.random() * 1000),
          errors: Math.floor(Math.random() * 10)
        };
      });
      setLiveMetrics(newMetrics);

      // Génération d'alertes aléatoires
      if (Math.random() < 0.1) {
        const componentIds = Object.keys(INFRASTRUCTURE.components);
        const randomComponent = componentIds[Math.floor(Math.random() * componentIds.length)];
        const alertTypes = ['high_cpu', 'high_memory', 'high_latency', 'error_rate'];
        const randomAlert = alertTypes[Math.floor(Math.random() * alertTypes.length)];
        
        setAlerts(prev => [{
          id: Date.now(),
          component: randomComponent,
          type: randomAlert,
          severity: Math.random() > 0.7 ? 'critical' : 'warning',
          message: `${randomAlert.replace('_', ' ')} detected on ${INFRASTRUCTURE.components[randomComponent].name}`,
          timestamp: new Date()
        }, ...prev.slice(0, 4)]);
      }
    };

    const interval = setInterval(updateMetrics, 2000);
    updateMetrics(); // Initial call
    return () => clearInterval(interval);
  }, []);

  // Composant Zone
  const Zone = ({ zone, data }) => (
    <div
      className="absolute border-2 border-dashed rounded-lg transition-all duration-300"
      style={{
        left: data.x,
        top: data.y,
        width: data.width,
        height: data.height,
        borderColor: data.color,
        backgroundColor: `${data.color}10`
      }}
    >
      <div 
        className="absolute -top-8 left-4 px-3 py-1 rounded-full text-sm font-bold text-white"
        style={{ backgroundColor: data.color }}
      >
        {data.name}
      </div>
    </div>
  );

  // Composant Infrastructure avancé
  const InfraComponent = ({ component }) => {
    const type = COMPONENT_TYPES[component.type];
    const metrics = liveMetrics[component.id] || {};
    const isSelected = selectedComponent === component.id;
    const hasAlert = alerts.some(alert => alert.component === component.id);

    return (
      <div
        className={`infrastructure-component absolute cursor-pointer transition-all duration-300 ${isSelected ? 'z-50' : 'z-10'}`}
        style={{
          left: component.x,
          top: component.y,
          transform: `scale(${isSelected ? 1.1 : 1})`
        }}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedComponent(isSelected ? null : component.id);
        }}
      >
        {/* Glow effect pour composant sélectionné */}
        {isSelected && (
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 opacity-20 blur-xl scale-110 animate-pulse" />
        )}
        
        {/* Alert indicator */}
        {hasAlert && (
          <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full animate-pulse z-10">
            <AlertTriangle className="w-3 h-3 text-white m-0.5" />
          </div>
        )}

        {/* Composant principal */}
        <div className={`relative w-48 bg-gradient-to-br ${type.bgGradient} p-4 rounded-2xl border border-white/20 backdrop-blur-sm shadow-2xl`}>
          {/* Header avec icône et statut */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{type.icon}</span>
              <div>
                <h3 className="text-white font-bold text-sm">{component.name}</h3>
                <p className="text-white/70 text-xs">{component.ip}</p>
              </div>
            </div>
            <div className={`w-3 h-3 rounded-full ${component.status === 'active' ? 'bg-green-400' : 'bg-red-400'} animate-pulse`} />
          </div>

          {/* Métriques temps réel */}
          {showMetrics && (
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="text-center">
                <div className="text-white/90 text-xs">CPU</div>
                <div className="text-white font-bold text-sm">{Math.round(metrics.cpu)}%</div>
              </div>
              <div className="text-center">
                <div className="text-white/90 text-xs">RAM</div>
                <div className="text-white font-bold text-sm">{Math.round(metrics.memory)}%</div>
              </div>
              <div className="text-center">
                <div className="text-white/90 text-xs">Req/s</div>
                <div className="text-white font-bold text-sm">{metrics.requests}</div>
              </div>
              <div className="text-center">
                <div className="text-white/90 text-xs">Latency</div>
                <div className="text-white font-bold text-sm">{Math.round(metrics.latency)}ms</div>
              </div>
            </div>
          )}

          {/* Barres de progression pour CPU et Memory */}
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Cpu className="w-3 h-3 text-white/70" />
              <div className="flex-1 bg-white/20 rounded-full h-1">
                <div 
                  className="bg-white h-1 rounded-full transition-all duration-500"
                  style={{ width: `${metrics.cpu}%` }}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <HardDrive className="w-3 h-3 text-white/70" />
              <div className="flex-1 bg-white/20 rounded-full h-1">
                <div 
                  className="bg-white h-1 rounded-full transition-all duration-500"
                  style={{ width: `${metrics.memory}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Composant de connexion animée
  const AnimatedConnection = ({ from, to, protocol = 'HTTPS' }) => {
    const fromComp = INFRASTRUCTURE.components[from];
    const toComp = INFRASTRUCTURE.components[to];
    const protocolInfo = PROTOCOLS[protocol];
    
    if (!fromComp || !toComp) return null;

    const startX = fromComp.x + 96; // centre du composant
    const startY = fromComp.y + 50;
    const endX = toComp.x + 96;
    const endY = toComp.y + 50;

    const pathId = `path-${from}-${to}`;

    return (
      <g className="connection-group">
        {/* Ligne de connexion */}
        <line
          x1={startX} y1={startY}
          x2={endX} y2={endY}
          stroke={protocolInfo.color}
          strokeWidth="3"
          strokeDasharray={protocolInfo.pattern === 'dashed' ? '10,5' : protocolInfo.pattern === 'dotted' ? '2,2' : 'none'}
          opacity="0.6"
          className="transition-all duration-300"
        />
        
        {/* Particules animées */}
        {!isPaused && Array.from({length: 3}).map((_, i) => (
          <circle key={i} r="4" fill={protocolInfo.color} opacity="0.8">
            <animateMotion
              dur={`${4 / animationSpeed}s`}
              repeatCount="indefinite"
              begin={`${i * (4 / animationSpeed / 3)}s`}
            >
              <mpath href={`#${pathId}`} />
            </animateMotion>
          </circle>
        ))}

        {/* Points de connexion */}
        <circle cx={startX} cy={startY} r="6" fill={protocolInfo.color} opacity="0.8" />
        <circle cx={endX} cy={endY} r="6" fill={protocolInfo.color} opacity="0.8" />

        {/* Label de protocole */}
        <g transform={`translate(${(startX + endX) / 2}, ${(startY + endY) / 2})`}>
          <rect
            x="-20" y="-12"
            width="40" height="24"
            fill="rgba(0,0,0,0.8)"
            rx="4"
            className="protocol-label"
          />
          <text
            textAnchor="middle"
            y="6"
            className="text-xs fill-white font-semibold"
          >
            {protocol}
          </text>
        </g>

        {/* Chemin invisible pour l'animation */}
        <defs>
          <path
            id={pathId}
            d={`M ${startX} ${startY} L ${endX} ${endY}`}
            fill="none"
          />
        </defs>
      </g>
    );
  };

  // Panel de détails d'un composant
  const ComponentDetailsPanel = () => {
    const component = INFRASTRUCTURE.components[selectedComponent];
    if (!component) return null;

    const metrics = liveMetrics[selectedComponent] || {};
    const componentAlerts = alerts.filter(alert => alert.component === selectedComponent);

    return (
      <div className="fixed right-4 top-24 w-96 bg-gray-900/95 backdrop-blur-sm border border-gray-700 rounded-2xl p-6 z-50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-white">{component.name}</h3>
          <button 
            onClick={() => setSelectedComponent(null)}
            className="text-gray-400 hover:text-white"
          >
            ✕
          </button>
        </div>
        
        {/* Métriques détaillées */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-blue-500/20 p-3 rounded-lg">
            <div className="text-blue-300 text-sm">CPU Usage</div>
            <div className="text-white text-2xl font-bold">{Math.round(metrics.cpu)}%</div>
          </div>
          <div className="bg-green-500/20 p-3 rounded-lg">
            <div className="text-green-300 text-sm">Memory</div>
            <div className="text-white text-2xl font-bold">{Math.round(metrics.memory)}%</div>
          </div>
          <div className="bg-purple-500/20 p-3 rounded-lg">
            <div className="text-purple-300 text-sm">Network</div>
            <div className="text-white text-2xl font-bold">{Math.round(metrics.network)}MB/s</div>
          </div>
          <div className="bg-yellow-500/20 p-3 rounded-lg">
            <div className="text-yellow-300 text-sm">Latency</div>
            <div className="text-white text-2xl font-bold">{Math.round(metrics.latency)}ms</div>
          </div>
        </div>

        {/* Alertes */}
        {componentAlerts.length > 0 && (
          <div className="mb-4">
            <h4 className="text-red-300 font-semibold mb-2">🚨 Alertes Actives</h4>
            {componentAlerts.map(alert => (
              <div key={alert.id} className="bg-red-500/20 border border-red-500/30 p-2 rounded-lg mb-2">
                <div className="text-red-300 text-sm">{alert.message}</div>
                <div className="text-red-400 text-xs">{alert.timestamp.toLocaleTimeString()}</div>
              </div>
            ))}
          </div>
        )}

        {/* Logiciels installés */}
        <div>
          <h4 className="text-white font-semibold mb-2">💿 Logiciels</h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {component.software.map((sw, idx) => (
              <div key={idx} className="flex items-center justify-between bg-gray-800/50 p-2 rounded-lg">
                <div>
                  <div className="text-white text-sm font-medium">{sw.name}</div>
                  <div className="text-gray-400 text-xs">v{sw.version}</div>
                </div>
                <div className="text-right">
                  <div className="text-green-400 text-xs">CPU: {sw.cpu}%</div>
                  <div className="text-blue-400 text-xs">RAM: {sw.memory}%</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: #0a0a0a;
          color: #ffffff;
          overflow: hidden;
        }
        
        /* Navigation */
        nav {
          position: fixed;
          top: 0;
          width: 100%;
          background: rgba(10, 10, 10, 0.95);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          z-index: 1000;
          padding: 1rem 2rem;
        }
        
        .nav-container {
          max-width: 1400px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .back-link {
          color: #667eea;
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: gap 0.3s;
        }
        
        .back-link:hover {
          gap: 1rem;
        }
        
        /* Toolbar */
        .toolbar {
          position: fixed;
          top: 80px;
          left: 20px;
          right: 20px;
          background: rgba(17, 24, 39, 0.95);
          backdrop-blur-sm;
          border: 1px solid rgba(75, 85, 99, 0.3);
          border-radius: 12px;
          padding: 12px 20px;
          z-index: 100;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
        }
        
        .toolbar-section {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .toolbar-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background: rgba(75, 85, 99, 0.3);
          border: 1px solid rgba(75, 85, 99, 0.5);
          border-radius: 8px;
          color: #d1d5db;
          text-decoration: none;
          font-size: 14px;
          transition: all 0.2s;
          cursor: pointer;
        }
        
        .toolbar-button:hover {
          background: rgba(99, 102, 241, 0.3);
          border-color: rgba(99, 102, 241, 0.5);
          color: white;
        }
        
        .toolbar-button.active {
          background: rgba(99, 102, 241, 0.5);
          border-color: rgba(99, 102, 241, 0.7);
          color: white;
        }
        
        /* Zoom controls */
        .zoom-controls {
          position: fixed;
          bottom: 20px;
          right: 20px;
          display: flex;
          gap: 8px;
          background: rgba(17, 24, 39, 0.95);
          backdrop-blur-sm;
          border: 1px solid rgba(75, 85, 99, 0.3);
          border-radius: 12px;
          padding: 8px;
          z-index: 100;
        }
        
        .zoom-button {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(75, 85, 99, 0.3);
          border: 1px solid rgba(75, 85, 99, 0.5);
          border-radius: 8px;
          color: #d1d5db;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .zoom-button:hover {
          background: rgba(99, 102, 241, 0.3);
          color: white;
        }
        
        /* Canvas container */
        .canvas-container {
          position: fixed;
          top: 140px;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(168, 85, 247, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 40% 80%, rgba(34, 197, 94, 0.1) 0%, transparent 50%),
                      linear-gradient(180deg, #0a0a0a 0%, #111827 100%);
          overflow: hidden;
        }
        
        /* Styles pour les animations */
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-5px); }
        }
        
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.3); }
          50% { box-shadow: 0 0 40px rgba(99, 102, 241, 0.6); }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-glow {
          animation: glow 2s ease-in-out infinite;
        }
        
        /* Legend */
        .legend {
          position: fixed;
          bottom: 20px;
          left: 20px;
          background: rgba(17, 24, 39, 0.95);
          backdrop-blur-sm;
          border: 1px solid rgba(75, 85, 99, 0.3);
          border-radius: 12px;
          padding: 16px;
          z-index: 100;
          min-width: 250px;
        }
        
        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          color: #d1d5db;
          font-size: 14px;
        }
        
        /* Search box */
        .search-box {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .search-input {
          width: 200px;
          padding: 8px 12px 8px 36px;
          background: rgba(31, 41, 55, 0.8);
          border: 1px solid rgba(75, 85, 99, 0.5);
          border-radius: 8px;
          color: white;
          font-size: 14px;
          transition: all 0.2s;
        }
        
        .search-input:focus {
          outline: none;
          border-color: rgba(99, 102, 241, 0.7);
          background: rgba(31, 41, 55, 1);
        }
        
        .search-icon {
          position: absolute;
          left: 10px;
          color: #9ca3af;
        }
        
        /* Alert panel */
        .alert-panel {
          position: fixed;
          top: 140px;
          left: 20px;
          width: 300px;
          max-height: 400px;
          background: rgba(17, 24, 39, 0.95);
          backdrop-blur-sm;
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 12px;
          z-index: 90;
          overflow: hidden;
        }
        
        .alert-header {
          padding: 16px;
          border-bottom: 1px solid rgba(75, 85, 99, 0.3);
          color: #ef4444;
          font-weight: 600;
        }
        
        .alert-list {
          max-height: 320px;
          overflow-y: auto;
        }
        
        .alert-item {
          padding: 12px;
          border-bottom: 1px solid rgba(75, 85, 99, 0.2);
          transition: background 0.2s;
        }
        
        .alert-item:hover {
          background: rgba(239, 68, 68, 0.1);
        }
      `}</style>

      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <a href="#" onClick={(e) => navigateToPage(e, 'index-page')} className="back-link">
            <ChevronLeft size={16} /> Retour à l'accueil
          </a>
          <div className="text-lg font-semibold">Infrastructure Documentation</div>
        </div>
      </nav>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-section">
          <button 
            className={`toolbar-button ${viewMode === 'topology' ? 'active' : ''}`}
            onClick={() => setViewMode('topology')}
          >
            <Activity size={16} />
            Topology
          </button>
          <button 
            className={`toolbar-button ${viewMode === 'logical' ? 'active' : ''}`}
            onClick={() => setViewMode('logical')}
          >
            <Layers size={16} />
            Logical
          </button>
          <button 
            className={`toolbar-button ${viewMode === 'physical' ? 'active' : ''}`}
            onClick={() => setViewMode('physical')}
          >
            <Server size={16} />
            Physical
          </button>
        </div>

        <div className="toolbar-section">
          <div className="search-box">
            <Search size={16} className="search-icon" />
            <input 
              type="text" 
              placeholder="Rechercher un composant..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="toolbar-section">
          <button 
            className={`toolbar-button ${showConnections ? 'active' : ''}`}
            onClick={() => setShowConnections(!showConnections)}
          >
            {showConnections ? <Eye size={16} /> : <EyeOff size={16} />}
            Connexions
          </button>
          <button 
            className={`toolbar-button ${showMetrics ? 'active' : ''}`}
            onClick={() => setShowMetrics(!showMetrics)}
          >
            <BarChart3 size={16} />
            Métriques
          </button>
          <button 
            className={`toolbar-button ${isPaused ? '' : 'active'}`}
            onClick={() => setIsPaused(!isPaused)}
          >
            {isPaused ? <Play size={16} /> : <Pause size={16} />}
            Animation
          </button>
        </div>
      </div>

      {/* Alert Panel */}
      {alerts.length > 0 && (
        <div className="alert-panel">
          <div className="alert-header">
            🚨 Alertes Actives ({alerts.length})
          </div>
          <div className="alert-list">
            {alerts.map(alert => (
              <div key={alert.id} className="alert-item">
                <div className="text-red-300 font-medium text-sm">{alert.message}</div>
                <div className="text-gray-400 text-xs mt-1">
                  {alert.timestamp.toLocaleTimeString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Canvas Container */}
      <div 
        className="canvas-container"
        onMouseDown={handleMouseDown}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div 
          className="relative w-full h-full"
          style={{
            transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            transition: isDragging ? 'none' : 'transform 0.3s ease'
          }}
        >
          {/* Zones de réseau */}
          {Object.entries(INFRASTRUCTURE.zones).map(([key, zone]) => (
            <Zone key={key} zone={key} data={zone} />
          ))}

          {/* Connexions SVG */}
          {showConnections && (
            <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 5 }}>
              <AnimatedConnection from="cdn" to="firewall" protocol="HTTPS" />
              <AnimatedConnection from="firewall" to="loadbalancer" protocol="HTTPS" />
              <AnimatedConnection from="loadbalancer" to="gateway" protocol="HTTP" />
              <AnimatedConnection from="loadbalancer" to="app1" protocol="HTTP" />
              <AnimatedConnection from="loadbalancer" to="app2" protocol="HTTP" />
              <AnimatedConnection from="gateway" to="app1" protocol="HTTP" />
              <AnimatedConnection from="gateway" to="app2" protocol="HTTP" />
              <AnimatedConnection from="app1" to="database" protocol="TCP" />
              <AnimatedConnection from="app2" to="database" protocol="TCP" />
              <AnimatedConnection from="app1" to="cache" protocol="TCP" />
              <AnimatedConnection from="app2" to="cache" protocol="TCP" />
              <AnimatedConnection from="app1" to="queue" protocol="TCP" />
              <AnimatedConnection from="app2" to="queue" protocol="TCP" />
              <AnimatedConnection from="database" to="database_replica" protocol="TCP" />
              <AnimatedConnection from="queue" to="monitoring" protocol="HTTP" />
            </svg>
          )}

          {/* Composants Infrastructure */}
          {Object.values(INFRASTRUCTURE.components)
            .filter(comp => searchTerm === '' || comp.name.toLowerCase().includes(searchTerm.toLowerCase()))
            .map(component => (
              <InfraComponent key={component.id} component={component} />
            ))}
        </div>
      </div>

      {/* Panel de détails */}
      <ComponentDetailsPanel />

      {/* Contrôles de zoom et navigation */}
      <div className="zoom-controls">
        <button 
          className="zoom-button"
          onClick={() => setZoom(Math.min(zoom + 0.1, 2))}
          title="Zoom avant"
        >
          <ZoomIn size={20} />
        </button>
        <button 
          className="zoom-button"
          onClick={() => setZoom(Math.max(zoom - 0.1, 0.5))}
          title="Zoom arrière"
        >
          <ZoomOut size={20} />
        </button>
        <button 
          className="zoom-button"
          onClick={centerView}
          title="Centrer la vue"
        >
          <Maximize size={20} />
        </button>
        <button 
          className="zoom-button"
          onClick={resetView}
          title="Reset vue"
        >
          <RotateCcw size={20} />
        </button>
        <div className="flex items-center px-3 text-sm text-gray-300">
          {Math.round(zoom * 100)}%
        </div>
      </div>

      {/* Contrôles directionnels */}
      <div className="navigation-controls">
        <div className="nav-row">
          <button 
            className="nav-button"
            onClick={() => setPanOffset(prev => ({ ...prev, y: prev.y - 50 }))}
            title="Haut"
          >
            ↑
          </button>
        </div>
        <div className="nav-row">
          <button 
            className="nav-button"
            onClick={() => setPanOffset(prev => ({ ...prev, x: prev.x - 50 }))}
            title="Gauche"
          >
            ←
          </button>
          <button 
            className="nav-button nav-center"
            onClick={centerView}
            title="Centre"
          >
            ⌂
          </button>
          <button 
            className="nav-button"
            onClick={() => setPanOffset(prev => ({ ...prev, x: prev.x + 50 }))}
            title="Droite"
          >
            →
          </button>
        </div>
        <div className="nav-row">
          <button 
            className="nav-button"
            onClick={() => setPanOffset(prev => ({ ...prev, y: prev.y + 50 }))}
            title="Bas"
          >
            ↓
          </button>
        </div>
      </div>

      {/* Légende */}
      <div className="legend">
        <div className="text-white font-semibold mb-3">🏗️ Légende</div>
        {Object.entries(COMPONENT_TYPES).slice(0, 6).map(([key, type]) => (
          <div key={key} className="legend-item">
            <span className="text-lg">{type.icon}</span>
            <span>{type.description}</span>
          </div>
        ))}
      </div>
    </>
  );
};

export default InfrastructureDocumentation;